// shell.ts — the DOM skin over the GPU world. Builds the masthead, the series
// index, and the reader overlay, then wires pointer/scroll/keyboard into the
// Cosmos scene so the background reacts to what the reader does.

import { POSTS, SERIES_TITLE, postBySlug, type Post } from "../blog/posts";
import { renderMarkdown } from "../blog/markdown";
import type { Cosmos } from "../scenes/cosmos";

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") node.className = v;
    else node.setAttribute(k, v);
  }
  for (const c of children) node.append(c);
  return node;
};

export class Shell {
  private readonly reader: HTMLElement;
  private readonly article: HTMLElement;
  private current: Post | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly cosmos: Cosmos,
  ) {
    this.root.append(this.masthead(), this.series());

    this.article = el("article", { class: "reader__article" });
    const close = el("button", { class: "reader__close", type: "button" }, "✕ close");
    close.addEventListener("click", () => this.closePost());
    this.reader = el("div", { class: "reader", "data-open": "false" }, close, this.article);
    document.body.append(this.reader);

    this.wireInput();
    this.routeFromHash();
    window.addEventListener("hashchange", () => this.routeFromHash());
  }

  private masthead(): HTMLElement {
    return el(
      "header",
      { class: "masthead" },
      el("h1", { class: "masthead__name" }, "aanya", el("em", {}, ".space")),
      el(
        "div",
        { class: "masthead__meta" },
        "rendered live · webgpu",
        el("br", {}),
        SERIES_TITLE,
      ),
    );
  }

  private series(): HTMLElement {
    const section = el("section", { class: "series" });
    section.append(el("div", { class: "series__kicker" }, `${SERIES_TITLE} — a series`));
    for (const post of POSTS) {
      const entry = el("a", { class: "entry", href: `#${post.slug}` });
      entry.append(
        el("div", { class: "entry__index" }, `№ ${post.index}  ·  ${post.date}`),
        el("h2", { class: "entry__title" }, post.title),
        el("p", { class: "entry__dek" }, post.dek),
      );
      entry.addEventListener("click", (e) => {
        e.preventDefault();
        this.openPost(post);
      });
      section.append(entry);
    }
    return section;
  }

  private wireInput(): void {
    window.addEventListener(
      "pointermove",
      (e) => {
        // normalize to -1..1, y up
        const x = (e.clientX / window.innerWidth) * 2 - 1;
        const y = -((e.clientY / window.innerHeight) * 2 - 1);
        this.cosmos.pointerTarget = [x, y];
      },
      { passive: true },
    );

    this.reader.addEventListener(
      "scroll",
      () => {
        const max = this.reader.scrollHeight - this.reader.clientHeight;
        this.cosmos.scrollTarget = max > 0 ? this.reader.scrollTop / max : 0;
      },
      { passive: true },
    );

    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.current) this.closePost();
    });
  }

  private routeFromHash(): void {
    const slug = location.hash.replace(/^#/, "");
    const post = slug ? postBySlug(slug) : undefined;
    if (post) this.openPost(post, /* fromHash */ true);
    else if (this.current) this.closePost(true);
  }

  private openPost(post: Post, fromHash = false): void {
    this.current = post;
    this.article.innerHTML =
      `<div class="reader__meta">№ ${post.index} · ${post.date}</div>` +
      renderMarkdown(post.body);
    this.reader.scrollTop = 0;
    this.reader.dataset.open = "true";
    this.cosmos.warpTarget = 1;
    this.cosmos.scrollTarget = 0;
    if (!fromHash && location.hash !== `#${post.slug}`) location.hash = post.slug;
  }

  private closePost(fromHash = false): void {
    this.current = null;
    this.reader.dataset.open = "false";
    this.cosmos.warpTarget = 0;
    if (!fromHash && location.hash) {
      history.pushState("", document.title, location.pathname + location.search);
    }
  }
}
